import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Helmet } from "react-helmet-async";
import { TrackingProvider } from "@/components/providers/TrackingProvider";
import { lazy, Suspense, type ReactNode } from "react";
import UrlNormalizer from "@/components/seo/UrlNormalizer";

// Critical path pages - load eagerly
import Index from "./pages/Index";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import RoofCertificateFlorida from "./pages/RoofCertificateFlorida";
import MicroSiteApp from "./MicroSiteApp";
import { detectBrandProfile } from "@/lib/brandRegistry";

// Domain-aware root: roofcertificateflorida.com serves the certificate landing page
const isCertificateDomain = () =>
  typeof window !== "undefined" &&
  window.location.hostname.toLowerCase().includes("roofcertificateflorida.com");

const isMicroSiteDomain = () => {
  if (typeof window === "undefined") return false;
  return detectBrandProfile().isMicroSite;
};

const RootRoute = () => {
  // Micro-site domains are fully isolated via MicroSiteApp below; this only
  // runs for the flagship O'Brien Contracting domain.
  return <Index />;
};


// On the certificate domain, isolate the entire app to the RCF brand routes:
//   /                           → landing page (RoofCertificateFlorida)
//   /:service                   → service hub (e.g. /wind-mitigation)
//   /:service/:city             → programmatic city × service page
//   *                           → fallback to landing page
const RcfServiceHub = lazy(() => import("./pages/rcf/RcfServiceHub"));
const RcfCityServicePage = lazy(() => import("./pages/rcf/RcfCityServicePage"));
const RcfCountyHub = lazy(() => import("./pages/rcf/RcfCountyHub"));
const RcfCarrierHub = lazy(() => import("./pages/rcf/RcfCarrierHub"));
const RcfCompare = lazy(() => import("./pages/rcf/RcfCompare"));
const RcfResource = lazy(() => import("./pages/rcf/RcfResource"));
const RcfAbout = lazy(() => import("./pages/rcf/RcfAbout"));
const RcfTrust = lazy(() => import("./pages/rcf/RcfTrust"));
const RcfEditorialPolicy = lazy(() => import("./pages/rcf/RcfEditorialPolicy"));
const RcfAuthors = lazy(() => import("./pages/rcf/RcfAuthors"));
const RcfChatWidget = lazy(() => import("./components/rcf/RcfChatWidget"));

const CertificateDomainApp = () => (
  <HelmetProvider>
    <BrowserRouter>
      <TrackingProvider>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-pulse text-muted-foreground">Loading…</div></div>}>
          <Routes>
            <Route path="/" element={<RoofCertificateFlorida />} />
            <Route path="/about" element={<RcfAbout />} />
            <Route path="/trust" element={<RcfTrust />} />
            <Route path="/editorial-policy" element={<RcfEditorialPolicy />} />
            <Route path="/authors" element={<RcfAuthors />} />
            <Route path="/county/:county" element={<RcfCountyHub />} />
            <Route path="/carrier/:carrier" element={<RcfCarrierHub />} />
            <Route path="/compare/:slug" element={<RcfCompare />} />
            <Route path="/resource/:slug" element={<RcfResource />} />
            <Route path="/:service/:city" element={<RcfCityServicePage />} />
            <Route path="/:service" element={<RcfServiceHub />} />
            <Route path="*" element={<RoofCertificateFlorida />} />
          </Routes>
          <RcfChatWidget />
        </Suspense>
      </TrackingProvider>
    </BrowserRouter>
  </HelmetProvider>
);

// Phase 3: Lazy load toasters - rarely used on first paint
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Lazy load non-critical pages for better initial bundle size
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const QuickEstimate = lazy(() => import("./pages/QuickEstimate"));
const LeakDetection = lazy(() => import("./pages/LeakDetection"));
const EmergencyRepair = lazy(() => import("./pages/EmergencyRepair"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const RoofVisualizer = lazy(() => import("./pages/RoofVisualizer"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const BeforeAfterGallery = lazy(() => import("./pages/BeforeAfterGallery"));
const Reviews = lazy(() => import("./pages/Reviews"));
const LeaveReview = lazy(() => import("./pages/LeaveReview"));
const LeaveAReview = lazy(() => import("./pages/LeaveAReview"));
const CounterAdLanding = lazy(() => import("./pages/CounterAdLanding"));

// Pillar & Hub Pages
const FloridaRoofingServices = lazy(() => import("./pages/FloridaRoofingServices"));
const ResidentialRoofing = lazy(() => import("./pages/ResidentialRoofing"));
const CommercialRoofing = lazy(() => import("./pages/CommercialRoofing"));
const CommercialPillar = lazy(() => import("./pages/CommercialPillar"));
const RoofInsuranceClaims = lazy(() => import("./pages/RoofInsuranceClaims"));
const RoofingMaterialsComparison = lazy(() => import("./pages/RoofingMaterialsComparison"));
const FloridaBuildingCodes = lazy(() => import("./pages/FloridaBuildingCodes"));

// Service Detail Pages
const MetalRoofing = lazy(() => import("./pages/services/MetalRoofing"));

// City Landing Pages
const SarasotaRoofing = lazy(() => import("./pages/SarasotaRoofing"));
const NorthPortRoofing = lazy(() => import("./pages/NorthPortRoofing"));
const NorthPortRoofingFl = lazy(() => import("./pages/local/NorthPortRoofingFl"));
const RoofingContractorNorthPortFl = lazy(() => import("./pages/local/RoofingContractorNorthPortFl"));
const RoofReplacementVeniceFl = lazy(() => import("./pages/local/RoofReplacementVeniceFl"));
const RoofReplacementSarasotaFl = lazy(() => import("./pages/local/RoofReplacementSarasotaFl"));
const MetalRoofingNaplesFl = lazy(() => import("./pages/local/MetalRoofingNaplesFl"));
const FortMyersRoofing = lazy(() => import("./pages/FortMyersRoofing"));
const CapeCoralRoofing = lazy(() => import("./pages/CapeCoralRoofing"));
const NaplesRoofing = lazy(() => import("./pages/NaplesRoofing"));
const VeniceRoofing = lazy(() => import("./pages/VeniceRoofing"));
const VeniceRoofingFl = lazy(() => import("./pages/local/VeniceRoofingFl"));
const BradentonRoofing = lazy(() => import("./pages/BradentonRoofing"));
const MarcoIslandRoofing = lazy(() => import("./pages/MarcoIslandRoofing"));
const BonitaSpringsRoofing = lazy(() => import("./pages/BonitaSpringsRoofing"));
const EsteroRoofing = lazy(() => import("./pages/EsteroRoofing"));
const PuntaGordaRoofing = lazy(() => import("./pages/PuntaGordaRoofing"));
const PortCharlotteRoofing = lazy(() => import("./pages/PortCharlotteRoofing"));
const TileRoofing = lazy(() => import("./pages/services/TileRoofing"));
const ShingleRoofing = lazy(() => import("./pages/services/ShingleRoofing"));
const FlatRoofing = lazy(() => import("./pages/services/FlatRoofing"));
const RoofInspection = lazy(() => import("./pages/services/RoofInspection"));
const RoofRepair = lazy(() => import("./pages/services/RoofRepair"));
const RoofReplacement = lazy(() => import("./pages/services/RoofReplacement"));
const RoofingServices = lazy(() => import("./pages/RoofingServices"));

// Phase 131: Neighborhood Landing Pages
const NeighborhoodLandingPage = lazy(() => import("./pages/NeighborhoodLandingPage"));
const NeighborhoodsHub = lazy(() => import("./pages/NeighborhoodsHub"));

// Phase 132: Zip Code Landing Pages
const ZipCodeLandingPage = lazy(() => import("./pages/ZipCodeLandingPage"));

// Phase 141: Press/Newsroom Page
const Press = lazy(() => import("./pages/Press"));

// Phase 212: Credentials Page
const Credentials = lazy(() => import("./pages/Credentials"));

// Phase 217 & 220: Partners & Editorial Guidelines
const Partners = lazy(() => import("./pages/Partners"));
const EditorialGuidelines = lazy(() => import("./pages/EditorialGuidelines"));

// Phase 151: Roof Quiz
const RoofQuiz = lazy(() => import("./pages/RoofQuiz"));

// Phase 158: Material Comparison Tool
const CompareMaterials = lazy(() => import("./pages/CompareMaterials"));

// Phase 170: Thank You Page
const ThankYou = lazy(() => import("./pages/ThankYou"));

// Phase 418: Review Thank You Page
const ReviewThankYou = lazy(() => import("./pages/ReviewThankYou"));

// Phase 142: Resource Pages
const HurricanePrepChecklist = lazy(() => import("./pages/resources/HurricanePrepChecklist"));
const ContractorHiringGuide = lazy(() => import("./pages/resources/ContractorHiringGuide"));
const RoofingGlossary = lazy(() => import("./pages/resources/RoofingGlossary"));

// Phase 251-260: Ultimate Guides & Tools
const MetalRoofingGuide = lazy(() => import("./pages/guides/MetalRoofingGuide"));
const TileRoofingGuide = lazy(() => import("./pages/guides/TileRoofingGuide"));
const ShingleRoofingGuide = lazy(() => import("./pages/guides/ShingleRoofingGuide"));
const FlatRoofingGuide = lazy(() => import("./pages/guides/FlatRoofingGuide"));
const RoofReplacementCostGuide = lazy(() => import("./pages/guides/RoofReplacementCostGuide"));
const InsuranceClaimGuide = lazy(() => import("./pages/guides/InsuranceClaimGuide"));
const WindMitigationGuide = lazy(() => import("./pages/guides/WindMitigationGuide"));
const SolarRoofingGuide = lazy(() => import("./pages/guides/SolarRoofingGuide"));
const WindMitigationInspection = lazy(() => import("./pages/WindMitigationInspection"));
const WindMitigationBrevard = lazy(() => import("./pages/WindMitigationBrevard"));
const WindMitigation4PointCombo = lazy(() => import("./pages/WindMitigation4PointCombo"));
const MaintenanceCalendar = lazy(() => import("./pages/resources/MaintenanceCalendar"));
const PermitGuide = lazy(() => import("./pages/resources/PermitGuide"));

// Phase 159: AI Storm Damage Analyzer
const StormDamageAnalyzer = lazy(() => import("./pages/StormDamageAnalyzer"));

// Phase 541-542: Comparison Pages
const CompareRouter = lazy(() => import("./pages/CompareRouter"));

// Phase 547 & 559: Cost Map & Near Me
const RoofingCostsByCity = lazy(() => import("./pages/RoofingCostsByCity"));
const RoofingNearMe = lazy(() => import("./pages/RoofingNearMe"));

// Phase 560-609: Roofer & Roofing Company Expansion
const RoofersNearCity = lazy(() => import("./pages/RoofersNearCity"));
const BestRoofersPage = lazy(() => import("./pages/BestRoofersPage"));
const CountyRoofing = lazy(() => import("./pages/CountyRoofing"));
const BocaRatonRoofing = lazy(() => import("./pages/BocaRatonRoofing"));
const DelrayBeachRoofing = lazy(() => import("./pages/DelrayBeachRoofing"));
const PalmBeachCountyRoofing = lazy(() => import("./pages/PalmBeachCountyRoofing"));
const BrowardCountyRoofing = lazy(() => import("./pages/BrowardCountyRoofing"));
const MiamiDadeCountyRoofing = lazy(() => import("./pages/MiamiDadeCountyRoofing"));
const ServiceCityPage = lazy(() => import("./pages/ServiceCityPage"));
const RoofRepairNearMe = lazy(() => import("./pages/RoofRepairNearMe"));
const RoofReplacementNearMe = lazy(() => import("./pages/RoofReplacementNearMe"));
const RoofInspectionNearMe = lazy(() => import("./pages/RoofInspectionNearMe"));

// Phase 610-809: Market Domination Expansion
const RoofingQuotesCity = lazy(() => import("./pages/RoofingQuotesCity"));
const RoofingContractorsCity = lazy(() => import("./pages/RoofingContractorsCity"));
const RoofEstimateCity = lazy(() => import("./pages/RoofEstimateCity"));
const EmergencyRoofingCity = lazy(() => import("./pages/EmergencyRoofingCity"));
const MetalRoofingNearMe = lazy(() => import("./pages/MetalRoofingNearMe"));
const TileRoofingNearMe = lazy(() => import("./pages/TileRoofingNearMe"));
const ShingleRoofingNearMe = lazy(() => import("./pages/ShingleRoofingNearMe"));
const FlatRoofingNearMe = lazy(() => import("./pages/FlatRoofingNearMe"));
const EmergencyRoofingNearMe = lazy(() => import("./pages/EmergencyRoofingNearMe"));
const QuoteFunnel = lazy(() => import("./pages/QuoteFunnel"));

// Phase 810-909: Multi-State Takeover
const TennesseeRoofingServices = lazy(() => import("./pages/TennesseeRoofingServices"));
const AlabamaRoofingServices = lazy(() => import("./pages/AlabamaRoofingServices"));
const SouthCarolinaRoofingServices = lazy(() => import("./pages/SouthCarolinaRoofingServices"));
const NorthCarolinaRoofingServices = lazy(() => import("./pages/NorthCarolinaRoofingServices"));
const StateCityLandingPage = lazy(() => import("./pages/StateCityLandingPage"));
const CompetitorComparison = lazy(() => import("./pages/CompetitorComparison"));
const BestRoofersState = lazy(() => import("./pages/BestRoofersState"));
const RoofingCompaniesNearMe = lazy(() => import("./pages/RoofingCompaniesNearMe"));
const AffordableRoofingCity = lazy(() => import("./pages/AffordableRoofingCity"));
const StormSeasonPage = lazy(() => import("./pages/StormSeasonPage"));
const CityReviewsPage = lazy(() => import("./pages/CityReviewsPage"));
const VideoTestimonials = lazy(() => import("./pages/VideoTestimonials"));
const WhyChooseUs = lazy(() => import("./pages/WhyChooseUs"));

// Phase 1510-1709: Florida Roof Intent Pages
const FloridaRoofIntentPage = lazy(() => import("./pages/FloridaRoofIntentPage"));

// Phase 552-555: Spanish Pages
const SpanishIndex = lazy(() => import("./pages/es/SpanishIndex"));
const SpanishContact = lazy(() => import("./pages/es/SpanishContact"));
const SpanishQuickEstimate = lazy(() => import("./pages/es/SpanishQuickEstimate"));
const SpanishServices = lazy(() => import("./pages/es/SpanishServices"));

// Phase 950-989: Spanish Expansion
const SpanishRoofingNearMe = lazy(() => import("./pages/es/SpanishRoofingNearMe"));
const SpanishEmergency = lazy(() => import("./pages/es/SpanishEmergency"));
const SpanishRoofRepair = lazy(() => import("./pages/es/SpanishRoofRepair"));
const SpanishRoofReplacement = lazy(() => import("./pages/es/SpanishRoofReplacement"));
const SpanishInsuranceClaims = lazy(() => import("./pages/es/SpanishInsuranceClaims"));
const SpanishCityLanding = lazy(() => import("./pages/es/SpanishCityLanding"));
// Linkable Asset Pages
const ToolsHub = lazy(() => import("./pages/ToolsHub"));
const FloridaRoofingReport = lazy(() => import("./pages/FloridaRoofingReport"));
const FAQHub = lazy(() => import("./pages/FAQHub"));

// Georgia Landing Pages
const GeorgiaLandingPage = lazy(() => import("./pages/GeorgiaLandingPage"));

// Admin Pages
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AIAgent = lazy(() => import("./pages/admin/AIAgent"));
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const BacklinkCommandCenter = lazy(() => import("./pages/admin/BacklinkCommandCenter"));
const BacklinkGrowthFactory = lazy(() => import("./pages/admin/BacklinkGrowthFactory"));
const ContentPerformance = lazy(() => import("./pages/admin/ContentPerformance"));
const EastCoastCoverage = lazy(() => import("./pages/admin/EastCoastCoverage"));
const SeoQA = lazy(() => import("./pages/admin/SeoQA"));
const SeoLocalCommandCenter = lazy(() => import("./pages/admin/SeoLocalCommandCenter"));
const RcfOutreach = lazy(() => import("./pages/admin/RcfOutreach"));
const RcfBacklinks = lazy(() => import("./pages/admin/RcfBacklinks"));
const GbpPerformance = lazy(() => import("./pages/admin/GbpPerformance"));
const NeighborhoodTakeover = lazy(() => import("./pages/admin/NeighborhoodTakeover"));
const MicrositeRoutes = lazy(() => import("./pages/admin/MicrositeRoutes"));
const MicrositeContent = lazy(() => import("./pages/admin/MicrositeContent"));
const SearchConsole = lazy(() => import("./pages/admin/SearchConsole"));
const GscPageAudit = lazy(() => import("./pages/admin/GscPageAudit"));
const VisitorIntelligence = lazy(() => import("./pages/admin/VisitorIntelligence"));
const BacklinkOutreach = lazy(() => import("./pages/admin/BacklinkOutreach"));
const PaaReview = lazy(() => import("./pages/admin/PaaReview"));
const InternalLinkGraph = lazy(() => import("./pages/admin/InternalLinkGraph"));
const ReviewVelocity = lazy(() => import("./pages/admin/ReviewVelocity"));
const CroLab = lazy(() => import("./pages/admin/CroLab"));

const GeorgiaRoofingServices = lazy(() => import("./pages/GeorgiaRoofingServices"));
const GeorgiaCommercialRoofing = lazy(() => import("./pages/GeorgiaCommercialRoofing"));
const GeorgiaBeforeAfterGallery = lazy(() => import("./pages/GeorgiaBeforeAfterGallery"));
const GeorgiaBuildingCodes = lazy(() => import("./pages/GeorgiaBuildingCodes"));
const GeorgiaInsuranceClaims = lazy(() => import("./pages/GeorgiaInsuranceClaims"));
const GeorgiaPermitGuide = lazy(() => import("./pages/resources/GeorgiaPermitGuide"));
const GeorgiaStormDamage = lazy(() => import("./pages/GeorgiaStormDamage"));
const GeorgiaResidentialRoofing = lazy(() => import("./pages/GeorgiaResidentialRoofing"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const NoIndexRoute = ({ children }: { children: ReactNode }) => (
  <>
    <Helmet>
      <meta name="robots" content="noindex, follow" />
    </Helmet>
    {children}
  </>
);

const queryClient = new QueryClient();

const App = () => {
  // Hard isolation: roofcertificateflorida.com renders ONLY the certificate landing.
  if (isCertificateDomain()) {
    return <CertificateDomainApp />;
  }
  // Hard isolation: each purchased domain (floridaroofreplacement.com,
  // florida-commercial-roofing.com, coatingkingz.com, etc.) renders ONLY its
  // own micro-site route tree — not the full OBC site.
  if (isMicroSiteDomain()) {
    return <MicroSiteApp />;
  }

  return (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        {/* Phase 3: Lazy loaded toasters with Suspense */}
        <Suspense fallback={null}>
          <Toaster />
          <Sonner />
        </Suspense>
        <BrowserRouter>
          <TrackingProvider>
            <UrlNormalizer />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Critical path routes - loaded eagerly */}
                <Route path="/" element={<RootRoute />} />
                {/* /home and /home/ collapse to the canonical root — replace-style so
                    the old path is not retained in browser history. This is a
                    client-side router redirect, not a server-side 301. A real 301
                    at the hosting layer is documented in docs/seo-external-actions.md. */}
                <Route path="/home" element={<Navigate to="/" replace />} />
                <Route path="/home/" element={<Navigate to="/" replace />} />
                <Route path="/florida-roof-certificate" element={<RoofCertificateFlorida />} />
                <Route path="/roof-certificate-florida" element={<RoofCertificateFlorida />} />
                <Route path="/roof_certificate_florida" element={<RoofCertificateFlorida />} />
                <Route path="/contact" element={<Contact />} />
                
                {/* Lazy loaded routes */}
                <Route path="/about" element={<About />} />
                <Route path="/about-us" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/roofing-services" element={<RoofingServices />} />
                <Route path="/quick-estimate" element={<QuickEstimate />} />
                <Route path="/leak-detection" element={<LeakDetection />} />
                <Route path="/emergency" element={<EmergencyRepair />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/roof-visualizer" element={<RoofVisualizer />} />
                <Route path="/sitemap" element={<Sitemap />} />
                
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:slug" element={<ProjectDetail />} />
                <Route path="/before-after" element={<BeforeAfterGallery />} />

                <Route path="/reviews" element={<Reviews />} />
                <Route path="/leave-review" element={<LeaveReview />} />
<Route path="/roofing" element={<ServiceAreas />} />
<Route path="/roofing/sarasota" element={<SarasotaRoofing />} />
<Route path="/roofing/north-port" element={<NorthPortRoofing />} />
<Route path="/roofing/north-port-fl" element={<NorthPortRoofingFl />} />
{/* City + Service landing pages (GBP geographic relevance) */}
<Route path="/roofing-contractor-north-port-fl" element={<RoofingContractorNorthPortFl />} />
<Route path="/roof-replacement-venice-fl" element={<RoofReplacementVeniceFl />} />
<Route path="/roof-replacement-sarasota-fl" element={<RoofReplacementSarasotaFl />} />
<Route path="/metal-roofing-naples-fl" element={<MetalRoofingNaplesFl />} />
{/* Phase 411: Priority City Landing Pages */}
<Route path="/roofing/fort-myers" element={<NoIndexRoute><FortMyersRoofing /></NoIndexRoute>} />
<Route path="/roofing/cape-coral" element={<NoIndexRoute><CapeCoralRoofing /></NoIndexRoute>} />
<Route path="/roofing/naples" element={<NoIndexRoute><NaplesRoofing /></NoIndexRoute>} />
<Route path="/roofing/venice" element={<VeniceRoofing />} />
<Route path="/roofing/venice-fl" element={<VeniceRoofingFl />} />
<Route path="/roofing/bradenton" element={<BradentonRoofing />} />
<Route path="/roofing/marco-island" element={<NoIndexRoute><MarcoIslandRoofing /></NoIndexRoute>} />
<Route path="/roofing/bonita-springs" element={<NoIndexRoute><BonitaSpringsRoofing /></NoIndexRoute>} />
<Route path="/roofing/estero" element={<NoIndexRoute><EsteroRoofing /></NoIndexRoute>} />
<Route path="/roofing/punta-gorda" element={<NoIndexRoute><PuntaGordaRoofing /></NoIndexRoute>} />
<Route path="/roofing/port-charlotte" element={<PortCharlotteRoofing />} />
{/* Georgia */}
<Route path="/roofing/georgia" element={<NoIndexRoute><GeorgiaRoofingServices /></NoIndexRoute>} />
<Route path="/roofing/georgia/commercial" element={<NoIndexRoute><GeorgiaCommercialRoofing /></NoIndexRoute>} />
<Route path="/roofing/georgia/residential" element={<NoIndexRoute><GeorgiaResidentialRoofing /></NoIndexRoute>} />
<Route path="/roofing/georgia/before-after" element={<NoIndexRoute><GeorgiaBeforeAfterGallery /></NoIndexRoute>} />
<Route path="/georgia-building-codes" element={<NoIndexRoute><GeorgiaBuildingCodes /></NoIndexRoute>} />
<Route path="/georgia-roof-insurance-claims" element={<NoIndexRoute><GeorgiaInsuranceClaims /></NoIndexRoute>} />
<Route path="/georgia-storm-damage" element={<NoIndexRoute><GeorgiaStormDamage /></NoIndexRoute>} />
{/* Georgia is OWNER_VERIFIED_ACTIVE (S2-OD-02 APPROVED). No GA state roofing license required for this scope; FL license labeled as such on GA-visible surfaces. Indexable. */}
<Route path="/roofing/ga/:citySlug/:neighborhoodSlug" element={<NeighborhoodLandingPage />} />
<Route path="/roofing/ga/:citySlug" element={<GeorgiaLandingPage />} />
{/* Phase 132: Zip code routes */}
<Route path="/roofing/zip/:zipCode" element={<ZipCodeLandingPage />} />
{/* Phase 131: Neighborhood-level routes (must come before city routes) */}
<Route path="/neighborhoods" element={<NeighborhoodsHub />} />
<Route path="/roofing/:citySlug/:neighborhoodSlug" element={<NeighborhoodLandingPage />} />
<Route path="/roofing/:slug" element={<LandingPage />} />
                
                {/* Phase 141: Press/Newsroom */}
                <Route path="/press" element={<Press />} />
                
                {/* Phase 212: Credentials */}
                <Route path="/credentials" element={<Credentials />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/editorial-guidelines" element={<EditorialGuidelines />} />
                
                {/* Phase 151: Roof Quiz */}
                <Route path="/roof-quiz" element={<RoofQuiz />} />
                
                {/* Phase 158: Material Comparison Tool */}
                <Route path="/compare-materials" element={<CompareMaterials />} />
                
{/* Phase 170: Thank You Page */}
<Route path="/thank-you" element={<ThankYou />} />

{/* Phase 418: Review Thank You Page */}
<Route path="/review-thank-you" element={<ReviewThankYou />} />
                
                {/* Phase 142: Resource Pages */}
                <Route path="/resources/hurricane-prep-checklist" element={<HurricanePrepChecklist />} />
                <Route path="/resources/contractor-hiring-guide" element={<ContractorHiringGuide />} />
                <Route path="/resources/roofing-glossary" element={<RoofingGlossary />} />
                <Route path="/resources/maintenance-calendar" element={<MaintenanceCalendar />} />
                <Route path="/resources/permit-guide" element={<PermitGuide />} />
                <Route path="/resources/georgia-permit-guide" element={<NoIndexRoute><GeorgiaPermitGuide /></NoIndexRoute>} />
                <Route path="/guides/metal-roofing" element={<MetalRoofingGuide />} />
                <Route path="/guides/tile-roofing" element={<TileRoofingGuide />} />
                <Route path="/guides/shingle-roofing" element={<ShingleRoofingGuide />} />
                <Route path="/guides/flat-roofing" element={<FlatRoofingGuide />} />
                <Route path="/guides/roof-replacement-cost" element={<RoofReplacementCostGuide />} />
                <Route path="/guides/insurance-claim-process" element={<InsuranceClaimGuide />} />
                <Route path="/guides/wind-mitigation" element={<WindMitigationGuide />} />
                <Route path="/wind-mitigation-inspection" element={<WindMitigationInspection />} />
                <Route path="/wind-mitigation-inspection/brevard-county" element={<WindMitigationBrevard />} />
                <Route path="/wind-mitigation-4-point-combo" element={<WindMitigation4PointCombo />} />
                <Route path="/guides/solar-roofing" element={<SolarRoofingGuide />} />
                
                {/* Phase 159: AI Storm Damage Analyzer */}
                <Route path="/storm-damage-analyzer" element={<StormDamageAnalyzer />} />
                
                {/* Phase 541-542: Comparison Pages */}
                <Route path="/compare/:slug" element={<CompareRouter />} />
                
                {/* Phase 547: Interactive Cost Map */}
                <Route path="/roofing-costs-by-city" element={<RoofingCostsByCity />} />
                
                {/* Phase 4: Linkable Asset Pages */}
                <Route path="/tools" element={<ToolsHub />} />
                <Route path="/roofing-faqs" element={<FAQHub />} />

                <Route path="/florida-roofing-report" element={<FloridaRoofingReport />} />
                
                {/* Phase 559: Roofing Near Me */}
                <Route path="/roofing-near-me" element={<RoofingNearMe />} />
                <Route path="/roofers-near-me" element={<RoofingNearMe />} />
                
                {/* Phase 560-569: Roofers in City pages */}
                <Route path="/roofers-in-:citySlug" element={<RoofersNearCity />} />
                
                {/* Phase 570-574: Near Me service variants */}
                <Route path="/roof-repair-near-me" element={<RoofRepairNearMe />} />
                <Route path="/roof-replacement-near-me" element={<RoofReplacementNearMe />} />
                <Route path="/roof-inspection-near-me" element={<RoofInspectionNearMe />} />
                
                {/* Phase 575-579: County pages */}
                <Route path="/roofing/collier-county" element={<NoIndexRoute><CountyRoofing /></NoIndexRoute>} />
                <Route path="/roofing/lee-county" element={<NoIndexRoute><CountyRoofing /></NoIndexRoute>} />
                <Route path="/roofing/charlotte-county" element={<CountyRoofing />} />
                <Route path="/roofing/sarasota-county" element={<CountyRoofing />} />
                <Route path="/roofing/manatee-county" element={<CountyRoofing />} />
                <Route path="/roofing/miami-dade-county" element={<MiamiDadeCountyRoofing />} />
                <Route path="/roofing/broward-county" element={<BrowardCountyRoofing />} />
                <Route path="/roofing/palm-beach-county" element={<PalmBeachCountyRoofing />} />
                <Route path="/roofing/boca-raton" element={<BocaRatonRoofing />} />
                <Route path="/roofing/delray-beach" element={<DelrayBeachRoofing />} />
                
                {/* Phase 580-584: Best Roofers pages */}
                <Route path="/best-roofers-:citySlug" element={<BestRoofersPage />} />
                
                {/* Phase 585-589: Service + City crossover pages */}
                <Route path="/metal-roofing-:citySlug" element={<ServiceCityPage />} />
                <Route path="/tile-roofing-:citySlug" element={<ServiceCityPage />} />
                <Route path="/shingle-roofing-:citySlug" element={<ServiceCityPage />} />
                <Route path="/roof-repair-:citySlug" element={<ServiceCityPage />} />
                <Route path="/roof-replacement-:citySlug" element={<ServiceCityPage />} />
                <Route path="/flat-roofing-:citySlug" element={<ServiceCityPage />} />
                <Route path="/roof-inspection-:citySlug" element={<ServiceCityPage />} />
                <Route path="/insurance-claims-:citySlug" element={<ServiceCityPage />} />
                
                {/* Phase 630-639: Roofing Quotes City Pages */}
                <Route path="/roofing-quotes-:citySlug" element={<RoofingQuotesCity />} />
                
                {/* Phase 640-649: Roofing Contractors City Pages */}
                <Route path="/roofing-contractors-:citySlug" element={<RoofingContractorsCity />} />
                
                {/* Phase 650-659: Roof Estimate City Pages */}
                <Route path="/roof-estimate-:citySlug" element={<RoofEstimateCity />} />
                
                {/* Phase 660-669: Emergency Roofing City Pages */}
                <Route path="/emergency-roofing-:citySlug" element={<EmergencyRoofingCity />} />
                
                {/* Phase 680-689: Material Near Me Pages */}
                <Route path="/metal-roofing-near-me" element={<MetalRoofingNearMe />} />
                <Route path="/tile-roofing-near-me" element={<TileRoofingNearMe />} />
                <Route path="/shingle-roofing-near-me" element={<ShingleRoofingNearMe />} />
                <Route path="/flat-roofing-near-me" element={<FlatRoofingNearMe />} />
                <Route path="/emergency-roofing-near-me" element={<EmergencyRoofingNearMe />} />
                
                {/* Phase 785-789: Quote Funnel */}
                <Route path="/get-quote" element={<QuoteFunnel />} />
                
                {/* Phase 810-909: Multi-State Takeover */}
                <Route path="/roofing/tennessee" element={<NoIndexRoute><TennesseeRoofingServices /></NoIndexRoute>} />
                <Route path="/roofing/alabama" element={<NoIndexRoute><AlabamaRoofingServices /></NoIndexRoute>} />
                <Route path="/roofing/south-carolina" element={<NoIndexRoute><SouthCarolinaRoofingServices /></NoIndexRoute>} />
                <Route path="/roofing/north-carolina" element={<NoIndexRoute><NorthCarolinaRoofingServices /></NoIndexRoute>} />
                <Route path="/roofing/tn/:citySlug" element={<NoIndexRoute><StateCityLandingPage /></NoIndexRoute>} />
                <Route path="/roofing/al/:citySlug" element={<NoIndexRoute><StateCityLandingPage /></NoIndexRoute>} />
                <Route path="/roofing/sc/:citySlug" element={<NoIndexRoute><StateCityLandingPage /></NoIndexRoute>} />
                <Route path="/roofing/nc/:citySlug" element={<NoIndexRoute><StateCityLandingPage /></NoIndexRoute>} />
                <Route path="/best-roofers-:stateSlug" element={<NoIndexRoute><BestRoofersState /></NoIndexRoute>} />
                <Route path="/roofing-companies-near-me" element={<RoofingCompaniesNearMe />} />
                <Route path="/affordable-roofing-:citySlug" element={<AffordableRoofingCity />} />
                <Route path="/hurricane-season-roofing" element={<StormSeasonPage />} />
                <Route path="/tornado-season-roofing" element={<StormSeasonPage />} />
                <Route path="/hail-season-roofing" element={<StormSeasonPage />} />
                <Route path="/reviews/:citySlug" element={<CityReviewsPage />} />
                <Route path="/video-testimonials" element={<VideoTestimonials />} />
                <Route path="/why-choose-us" element={<WhyChooseUs />} />
                
                {/* Phase 552-555: Spanish Pages */}
                <Route path="/es" element={<SpanishIndex />} />
                <Route path="/es/contacto" element={<SpanishContact />} />
                <Route path="/es/presupuesto-gratis" element={<SpanishQuickEstimate />} />
                <Route path="/es/servicios" element={<SpanishServices />} />
                
                {/* Phase 950-989: Spanish Expansion */}
                <Route path="/es/techos-cerca-de-mi" element={<SpanishRoofingNearMe />} />
                <Route path="/es/reparacion-de-techos-emergencia" element={<SpanishEmergency />} />
                <Route path="/es/reparacion-de-techos" element={<SpanishRoofRepair />} />
                <Route path="/es/reemplazo-de-techo" element={<SpanishRoofReplacement />} />
                <Route path="/es/reclamaciones-de-seguro" element={<SpanishInsuranceClaims />} />
                <Route path="/es/techos-:citySlug" element={<SpanishCityLanding />} />
                
                {/* Pillar & Hub Pages */}
                <Route path="/florida-roofing-services" element={<FloridaRoofingServices />} />
                <Route path="/residential-roofing" element={<ResidentialRoofing />} />
                <Route path="/commercial-roofing" element={<CommercialRoofing />} />
                <Route path="/commercial-roofing-services" element={<CommercialPillar />} />
                <Route path="/roof-insurance-claims" element={<RoofInsuranceClaims />} />
                <Route path="/roofing-materials-comparison" element={<RoofingMaterialsComparison />} />
                <Route path="/florida-building-codes" element={<FloridaBuildingCodes />} />
                
                {/* Service Detail Pages */}
                <Route path="/services/metal-roofing" element={<MetalRoofing />} />
                <Route path="/metal-roofing" element={<MetalRoofing />} />
                <Route path="/services/tile-roofing" element={<TileRoofing />} />
                <Route path="/tile-roofing" element={<TileRoofing />} />
                <Route path="/services/shingle-roofing" element={<ShingleRoofing />} />
                <Route path="/shingle-roofing" element={<ShingleRoofing />} />
                <Route path="/services/flat-roofing" element={<FlatRoofing />} />
                <Route path="/flat-roofing" element={<FlatRoofing />} />
                <Route path="/services/roof-inspection" element={<RoofInspection />} />
                <Route path="/roof-inspection" element={<RoofInspection />} />
                <Route path="/roof-repair" element={<RoofRepair />} />
                <Route path="/roof-replacement" element={<RoofReplacement />} />
                
                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminDashboard />}>
                  <Route index element={<AIAgent />} />
                  <Route path="overview" element={<AdminOverview />} />
                  <Route path="backlinks" element={<BacklinkCommandCenter />} />
                  <Route path="growth" element={<BacklinkGrowthFactory />} />
                  <Route path="content" element={<ContentPerformance />} />
                  <Route path="east-coast" element={<EastCoastCoverage />} />
                  <Route path="seo-qa" element={<SeoQA />} />
                  <Route path="seo-local-command-center" element={<SeoLocalCommandCenter />} />
                  <Route path="rcf-outreach" element={<RcfOutreach />} />
                  <Route path="rcf-backlinks" element={<RcfBacklinks />} />
                  <Route path="gbp-performance" element={<GbpPerformance />} />
                  <Route path="neighborhood-takeover" element={<NeighborhoodTakeover />} />
                  <Route path="microsite-routes" element={<MicrositeRoutes />} />
                  <Route path="microsite-content" element={<MicrositeContent />} />
                  <Route path="search-console" element={<SearchConsole />} />
                  <Route path="gsc-page-audit" element={<GscPageAudit />} />
                  <Route path="visitor-intelligence" element={<VisitorIntelligence />} />
                  <Route path="backlink-outreach" element={<BacklinkOutreach />} />
                  <Route path="paa-review" element={<PaaReview />} />
                  <Route path="internal-link-graph" element={<InternalLinkGraph />} />
                  <Route path="review-velocity" element={<ReviewVelocity />} />
                  <Route path="cro-lab" element={<CroLab />} />
                </Route>


                {/* Phase 1510-1709: Florida Roof Intent Pages */}
                <Route path="/florida/:intentType-:citySlug" element={<FloridaRoofIntentPage />} />

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </TrackingProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
